import { LeadStage } from './leads-board.component';

/** A declared transition, as `/api/lead-stages/transitions` returns it. */
export interface LeadStageTransition {
  fromLeadStageId: number;
  toLeadStageId: number;
}

/**
 * Where a lead standing in one stage may be moved to.
 *
 * The server owns this rule — `LeadStageMoves` on the backend, enforced in
 * `ChangeStageAsync` — and this is the client's reading of the same edges, so
 * a screen offers a move exactly when the command would accept it. The board
 * and the drawer each used to filter the stage list by hand, which agreed with
 * the server only for the lead vocabulary and only by luck.
 *
 * Two rules combine, in this order:
 *
 * 1. A winning stage is never a target — WHERE THE SERVER SAYS SO. A lead
 *    reaches one by its contract being signed, and `LeadPipelineLogic`
 *    refuses the move before it looks at any declared edge. Asked of the
 *    outcome, never of the name.
 *
 *    Roll-off does NOT have that guard, deliberately: nothing drives a hire's
 *    stage off a document, so `RollOffOpportunityLogic` argues at length that
 *    the same rule would make Fulfillment unreachable by any means, park every
 *    hire in Prepayment and permanently zero the won figure. Applying it there
 *    would make this function stricter than the command it claims to mirror,
 *    which is the one way a client-side copy of a server rule does real harm:
 *    it hides a move the API would have accepted, and nothing errors.
 *
 *    So it is a parameter, named for the policy rather than for the
 *    mechanism, and its default is the lead pipeline's - which is what every
 *    caller written before roll-off existed meant.
 * 2. If the vocabulary declares any transitions at all it is CONSTRAINED, and
 *    only its declared edges are offered. Otherwise every other active stage
 *    in it is, which is how leads have always moved.
 *
 * Being constrained is a property of the vocabulary, not of the stage in hand:
 * roll-off's Fulfillment and Lost end the pursuit and declare no way out, and
 * that empty answer must read as "nowhere from here" rather than as "no rule".
 */
export function legalMoveCodes(
  stages: LeadStage[],
  transitions: LeadStageTransition[],
  fromStageCode: string | null | undefined,
  /** False where the pipeline lets a person say the deal was won. */
  winningStageNeedsEvidence = true,
): string[] {
  const from = stages.find(stage => stage.code === fromStageCode);
  if (!from) return [];

  const inVocabulary = stages.filter(
    stage => stage.vocabulary === from.vocabulary && stage.isActive);
  const ids = new Set(inVocabulary.map(stage => stage.id));

  // "Does anything in this vocabulary declare a way out" - answered across the
  // whole vocabulary, which is why the edge list arrives whole.
  const declared = transitions.filter(edge => ids.has(edge.fromLeadStageId));
  const constrained = declared.length > 0;

  const reachable = constrained
    ? new Set(declared
        .filter(edge => edge.fromLeadStageId === from.id)
        .map(edge => edge.toLeadStageId))
    : null;

  return inVocabulary
    .filter(stage =>
      stage.id !== from.id &&
      (!winningStageNeedsEvidence || stage.outcome !== 'won') &&
      (reachable === null || reachable.has(stage.id)))
    .map(stage => stage.code);
}
